export const SITE_NAME = 'Empire Maps Live';

export const SITE_URL = (
    process.env.REACT_APP_SITE_URL || 'https://empiremaps.live'
).replace(/\/$/, '');

export const DEFAULT_TITLE =
    'Empire Maps Live — Sports Imperialism Territory Maps';

export const DEFAULT_DESCRIPTION =
    'NCAA men\'s Division I basketball imperialism map. Every win transfers territory across America — live conquest chains for college basketball, plus NCAA football, NBA, NHL, NFL, MLB, and MLS.';

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/** Per-league OG share image (generated by scripts/generate-og.js). */
export function leagueOgImage(leagueId: string): string {
    return LEAGUE_SEO[leagueId] ? `${SITE_URL}/og/${leagueId}.png` : DEFAULT_OG_IMAGE;
}

/**
 * Origin of the API service (where dynamic OG images + crawler share/unfurl
 * pages are served). Derived from REACT_APP_API_URL; falls back to the current
 * origin in dev (where /api and /share are proxied to the backend).
 */
export const API_ORIGIN = (() => {
    const base = process.env.REACT_APP_API_URL || '';
    try {
        return base ? new URL(base, window.location.origin).origin : window.location.origin;
    } catch {
        return window.location.origin;
    }
})();

/** Dynamically rendered per-rivalry OG image (PNG, served by the API). */
export function rivalryOgImage(leagueId: string, teamAId: number, teamBId: number): string {
    return `${API_ORIGIN}/api/v2/og/rivalry/${encodeURIComponent(leagueId)}/${teamAId}/${teamBId}.png`;
}

/** Live territory map still (choropleth + logos) for homepage / share. */
export function homeMapOgImage(leagueId: string): string {
    return `${API_ORIGIN}/api/v2/og/map/${encodeURIComponent(leagueId)}.png`;
}

/**
 * Crawler-facing share URL for a rivalry. This API-hosted page carries the
 * per-rivalry OG meta and instantly redirects humans to the SPA route — use it
 * as the shared link so social/iMessage/Discord unfurls show the matchup card.
 */
export function rivalryShareUrl(leagueId: string, teamAId: number, teamBId: number): string {
    return `${API_ORIGIN}/share/rivalry/${encodeURIComponent(leagueId)}/${teamAId}/${teamBId}`;
}

export interface PageSEO {
    title?: string;
    description?: string;
    path?: string;
    noindex?: boolean;
    image?: string;
}

export const LEAGUE_SEO: Record<string, { title: string; description: string }> = {
    ncaa_basketball: {
        title: 'NCAA Basketball Imperialism Map',
        description:
            'Live NCAA basketball imperialism map. Every March Madness win transfers counties — track the conquest chain from opening day to the national champion.',
    },
    ncaa_football: {
        title: 'NCAA Football Imperialism Map',
        description:
            'College football imperialism map updated after every game. See which team owns America and how bowl season reshapes the territory.',
    },
    nba: {
        title: 'NBA Imperialism Map',
        description:
            'NBA imperialism territory map. Follow how each win shifts counties across the United States through the regular season and playoffs.',
    },
    nhl: {
        title: 'NHL Imperialism Map',
        description:
            'NHL imperialism territory map. Every win transfers counties — track which franchise rules America through the regular season and Stanley Cup playoffs.',
    },
    nfl: {
        title: 'NFL Imperialism Map',
        description:
            'NFL imperialism map — every victory conquers rival territory. Track which franchise rules America week by week.',
    },
    mlb: {
        title: 'MLB Imperialism Map',
        description:
            'MLB imperialism territory map with market-weighted opening empires. Every win transfers counties — follow the baseball conquest chain all season.',
    },
    mls: {
        title: 'MLS Imperialism Map',
        description:
            'MLS imperialism territory map. Every win transfers counties — track which club rules America from February through MLS Cup.',
    },
};

export function getLeagueSEO(leagueId: string, leagueName?: string): PageSEO {
    const preset = LEAGUE_SEO[leagueId];
    const name = leagueName || preset?.title.replace(' Imperialism Map', '') || 'League';

    return {
        title: preset?.title ?? `${name} Imperialism Map`,
        description:
            preset?.description ??
            `Live ${name} imperialism territory map on Empire Maps. Every game transfers land — follow the conquest chain in real time.`,
        path: `/league/${leagueId}`,
        image: leagueOgImage(leagueId),
    };
}

export function buildTitle(pageTitle?: string): string {
    if (!pageTitle) return DEFAULT_TITLE;
    return `${pageTitle} | ${SITE_NAME}`;
}

export function absoluteUrl(path = ''): string {
    if (!path || path === '/') return SITE_URL;
    return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export const PUBLIC_ROUTES = [
    { path: '/', changefreq: 'daily', priority: '1.0' },
    { path: '/about', changefreq: 'monthly', priority: '0.8' },
    { path: '/how-it-works', changefreq: 'monthly', priority: '0.8' },
    { path: '/guides', changefreq: 'weekly', priority: '0.85' },
    { path: '/privacy', changefreq: 'yearly', priority: '0.5' },
    { path: '/terms', changefreq: 'yearly', priority: '0.5' },
    { path: '/stats', changefreq: 'weekly', priority: '0.8' },
    { path: '/forum', changefreq: 'daily', priority: '0.7' },
    { path: '/feed', changefreq: 'daily', priority: '0.75' },
    { path: '/survivor', changefreq: 'weekly', priority: '0.7' },
    { path: '/ufc', changefreq: 'daily', priority: '0.85' },
    { path: '/naming-wars', changefreq: 'weekly', priority: '0.85' },
    { path: '/league/ncaa_basketball', changefreq: 'daily', priority: '0.9' },
    { path: '/league/ncaa_football', changefreq: 'daily', priority: '0.9' },
    { path: '/league/nba', changefreq: 'daily', priority: '0.9' },
    { path: '/league/nhl', changefreq: 'daily', priority: '0.9' },
    { path: '/league/nfl', changefreq: 'daily', priority: '0.9' },
    { path: '/league/mlb', changefreq: 'daily', priority: '0.9' },
    { path: '/league/mls', changefreq: 'daily', priority: '0.9' },
    { path: '/guides/ncaa-basketball-imperialism-map', changefreq: 'monthly', priority: '0.75' },
    { path: '/guides/ncaa-football-imperialism-map', changefreq: 'monthly', priority: '0.75' },
    { path: '/guides/nba-imperialism-map', changefreq: 'monthly', priority: '0.75' },
    { path: '/guides/nhl-imperialism-map', changefreq: 'monthly', priority: '0.75' },
    { path: '/guides/nfl-imperialism-map', changefreq: 'monthly', priority: '0.75' },
    { path: '/guides/mlb-imperialism-map', changefreq: 'monthly', priority: '0.75' },
    { path: '/guides/mls-imperialism-map', changefreq: 'monthly', priority: '0.75' },
] as const;

export function getHomepageJsonLd(): Record<string, unknown> {
    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebSite',
                '@id': `${SITE_URL}/#website`,
                url: SITE_URL,
                name: SITE_NAME,
                description: DEFAULT_DESCRIPTION,
                potentialAction: {
                    '@type': 'SearchAction',
                    target: {
                        '@type': 'EntryPoint',
                        urlTemplate: `${SITE_URL}/stats?q={search_term_string}`,
                    },
                    'query-input': 'required name=search_term_string',
                },
            },
            {
                '@type': 'WebApplication',
                '@id': `${SITE_URL}/#app`,
                name: SITE_NAME,
                url: SITE_URL,
                applicationCategory: 'SportsApplication',
                operatingSystem: 'Web',
                description: DEFAULT_DESCRIPTION,
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
            },
            {
                '@type': 'Organization',
                '@id': `${SITE_URL}/#organization`,
                name: SITE_NAME,
                url: SITE_URL,
                logo: `${SITE_URL}/favicon.svg`,
            },
        ],
    };
}

/** Stable reference for React effect dependencies */
export const HOMEPAGE_JSON_LD = getHomepageJsonLd();

export function getLeagueJsonLd(
    leagueId: string,
    leagueName: string,
    description: string,
): Record<string, unknown> {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `${leagueName} Imperialism Map`,
        description,
        url: absoluteUrl(`/league/${leagueId}`),
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: {
            '@type': 'SportsOrganization',
            name: leagueName,
        },
    };
}
